// Compare Modal — lets user pick 2 sets of layers to compare
import { useState, useEffect } from 'react';
import { projectsApi, layersApi, type Layer } from '@/api/mapApi';
import { CompareView } from './CompareView';

interface Props {
  onClose: () => void;
}

interface ProjectOption {
  id: string;
  name: string;
  layers: Layer[];
}

export function CompareModal({ onClose }: Props) {
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [oldProjectId, setOldProjectId] = useState<string>('');
  const [newProjectId, setNewProjectId] = useState<string>('');
  const [oldLayerIds, setOldLayerIds] = useState<string[]>([]);
  const [newLayerIds, setNewLayerIds] = useState<string[]>([]);
  const [mode, setMode] = useState<'slider' | 'overlay' | 'side_by_side'>('slider');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const list = await projectsApi.list();
      const publicProjects = (list.items ?? []).filter((p: any) => p.is_public);
      const withLayers = await Promise.all(
        publicProjects.map(async (p: any) => {
          const layers = await layersApi.listForProject(p.id);
          return { id: p.id, name: p.name, layers: layers.items };
        })
      );
      setProjects(withLayers);
      if (withLayers.length >= 2) {
        setOldProjectId(withLayers[0].id);
        setNewProjectId(withLayers[1].id);
        if (withLayers[0].layers.length > 0) setOldLayerIds([withLayers[0].layers[0].id]);
        if (withLayers[1].layers.length > 0) setNewLayerIds([withLayers[1].layers[0].id]);
      }
    } finally {
      setLoading(false);
    }
  }

  function toggleLayer(side: 'old' | 'new', layerId: string) {
    const setter = side === 'old' ? setOldLayerIds : setNewLayerIds;
    const current = side === 'old' ? oldLayerIds : newLayerIds;
    setter(current.includes(layerId) ? current.filter((id) => id !== layerId) : [...current, layerId]);
  }

  function startCompare() {
    if (oldLayerIds.length === 0 || newLayerIds.length === 0) {
      alert('Vui lòng chọn ít nhất 1 layer mỗi bên');
      return;
    }
    setActive(true);
  }

  const oldProject = projects.find((p) => p.id === oldProjectId);
  const newProject = projects.find((p) => p.id === newProjectId);

  if (active) {
    const oldLayers = (oldProject?.layers ?? []).filter((l) => oldLayerIds.includes(l.id));
    const newLayers = (newProject?.layers ?? []).filter((l) => newLayerIds.includes(l.id));
    return (
      <CompareView
        mode={{
          type: mode,
          oldLayers: oldLayers.map((l) => ({ id: l.id, geojsonKey: l.geojsonKey ?? '', name: l.name, style: l.style })),
          newLayers: newLayers.map((l) => ({ id: l.id, geojsonKey: l.geojsonKey ?? '', name: l.name, style: l.style })),
        }}
        onClose={onClose}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">So sánh quy hoạch</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">×</button>
        </div>

        {loading && <p className="text-sm text-gray-500">Đang tải dữ liệu...</p>}

        {!loading && projects.length < 2 && (
          <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
            Cần ít nhất 2 đồ án công khai để so sánh. Hiện có {projects.length} đồ án public.
          </div>
        )}

        {!loading && projects.length >= 2 && (
          <>
            <p className="text-sm text-gray-600 mb-4">
              Chọn các layers cần so sánh. Cùng 1 layer có thể thuộc nhiều bản (cũ/mới).
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="border-2 border-blue-200 rounded-lg p-3 bg-blue-50">
                <p className="font-semibold text-blue-900 mb-2">📂 Bản cũ</p>
                <select
                  value={oldProjectId}
                  onChange={(e) => { setOldProjectId(e.target.value); setOldLayerIds([]); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {oldProject?.layers.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={oldLayerIds.includes(l.id)}
                        onChange={() => toggleLayer('old', l.id)}
                        className="rounded"
                      />
                      <span>{l.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="border-2 border-red-200 rounded-lg p-3 bg-red-50">
                <p className="font-semibold text-red-900 mb-2">📂 Bản mới</p>
                <select
                  value={newProjectId}
                  onChange={(e) => { setNewProjectId(e.target.value); setNewLayerIds([]); }}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm mb-2"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {newProject?.layers.map((l) => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newLayerIds.includes(l.id)}
                        onChange={() => toggleLayer('new', l.id)}
                        className="rounded"
                      />
                      <span>{l.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Chế độ hiển thị:</p>
              <div className="flex gap-2">
                {(['slider', 'overlay', 'side_by_side'] as const).map((m) => (
                  <button
                    key={m}
                    onClick={() => setMode(m)}
                    className={`flex-1 px-3 py-2 text-sm border rounded ${
                      mode === m ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    {m === 'slider' ? '🔲 Slider' : m === 'overlay' ? '🔳 Overlay' : '⬜ Side-by-side'}
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={startCompare}
              disabled={oldLayerIds.length === 0 || newLayerIds.length === 0}
              className="w-full bg-brand-600 text-white py-2 rounded-md hover:bg-brand-700 disabled:opacity-50 font-medium"
            >
              Bắt đầu so sánh ({oldLayerIds.length + newLayerIds.length} layers)
            </button>
          </>
        )}
      </div>
    </div>
  );
}
